import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { menuInfo } from "@shared/type/CommonType";
import { global } from "@shared/libs/Global";
import { applySlip, getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { fnGetDataSet, fnGetTableById, fnSave } from "@shared/api/restApi";
import { dateToString } from "@shared/utils/DateUtils";
import { showResizablePopupModal } from "@widgets/CommonModal";
import SN_SCT_SIO_REG_001_POP_001 from "./SN_SCT_SIO_REG_001_POP_001";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import { RowNode } from "ag-grid-enterprise";
import SearchPanel from "@widgets/SearchPanel";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { SnButton } from "@shared/components/SnButton";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";

interface Props {
  Menuprops: menuInfo;
}

// 외주출고요청
const SN_SCT_SIO_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });

  const iotypeRef = useRef<any[]>([]);
  const [selectedDocu, setSelectedDocu] = useState<string>("");

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });

        const payload = {
          id: "iotype",
          params: { cd_firm: userInfo.cd_firm, tp_slip: "5", fg_slip: "2" },
        };
        const data = await fnGetTableById(payload);
        if (Array.isArray(data) && data.length > 0) {
          iotypeRef.current = data;
        }
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_sct_sio_reg_001_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = () => {
    try {
      gridRefs.current["grid1"].current?.addRow();
    } catch (error: any) {}
  };

  const onBeforeAddRow1 = (): any => {
    try {
      const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("alter", "수정된 내용이 있습니다.");
        return false;
      }

      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      return {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_biz,
        cd_cust: searchInfo.cd_cust,
        nm_cust_sort: searchInfo.nm_cust_sort,
        dt_req: today,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        cd_gp: userInfo?.cd_gp,
        cd_dept: userInfo?.cd_dept,
        cd_io: "510",
        fg_isu: "1",
      };
    } catch (error: any) {
      return false;
    }
  };

  const onAddRow = () => {
    try {
      return gridRefs.current["grid2"].current?.addRow();
    } catch (error: any) {}
  };

  const onBeforeAddRow2 = (): any => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const today = dateToString(new Date());
      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line");
      return {
        cd_firm: focusedNodeH.data?.cd_firm,
        cd_biz: focusedNodeH.data?.cd_biz,
        no_req: focusedNodeH.data?.no_req,
        no_line: maxNoLine + 1,
        dt_dlv: today,
        qt_isu: 0,
        fg_matl: "1",
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    let lineList: any[] = [];
    gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
      lineList.push(node);
    });
    for (const l of lineList) {
      if (l.data.qt_isu > 0) {
        await showDialog("alter", "이미 진행된 건 입니다.");
        return;
      }
    }

    gridRefs.current["grid2"].current.deleteAllRows();
    gridRefs.current["grid1"].current.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow = async () => {
    const rowCount = gridRefs.current["grid2"].current?.getDisplayedRowCount();
    if (rowCount === 0) return;

    const focusedNodeL = gridRefs.current["grid2"].current.focusedRowNode();
    if (focusedNodeL?.data.qt_isu > 0) {
      await showDialog("alter", "이미 진행된 건 입니다.");
      return;
    }

    gridRefs.current["grid2"].current?.deleteSelectedRows();
    if (rowCount === 1) {
      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      gridRefs.current["grid1"].current.clearBlock(focusedRowNodeH);
    }
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      gridRefs.current["grid2"].current.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const isuReqHChanges = gridRefs.current["grid1"].current.getChange();
      const isuReqLChanges = gridRefs.current["grid2"].current.getChange();
      const headerInserts = isuReqHChanges.insert ?? [];
      const lineInserts = isuReqLChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      for (const h of headerInserts) {
        if (!h.cd_cust) {
          await showDialog("alter", "외주처은(는) 필수값입니다.");
          return;
        }
        if (!h.cd_wh) {
          await showDialog("alter", "출고창고은(는) 필수값입니다.");
          return;
        }
      }

      if (
        isuReqHChanges.modifiedRowcount === 0 &&
        isuReqLChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      const noSlip = await applySlip({
        cd_column: "no_req",
        cd_module: Menuprops.cd_module,
        no_line: 5,
        cd_prefix: "",
        dt: focusedRowNodeH.data.dt_req,
        girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
        inserts: [isuReqHChanges, isuReqLChanges],
        cd_menu: Menuprops.cd_menu,
      });

      const saveData = {
        isuReqH: isuReqHChanges,
        isuReqL: isuReqLChanges,
      };
      const ok = await fnSave("SN_SCT_SIO_REG_001_SAVE_01", saveData);
      if (!ok) {
        gridRefs.current["grid2"].current.resetUpdate(true);
        gridRefs.current["grid1"].current.resetUpdate(true);
        return false;
      }

      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid1"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid2"].current.resetUpdate(true);
      gridRefs.current["grid1"].current.resetUpdate(true);
      return false;
    }
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id == "btnApply") {
      const isuReqHChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (isuReqHChanges.length === 0) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const searchInfo = searchRef.current.getFormdata();
      const selectedData = {
        cd_biz: searchInfo.cd_biz,
        cd_cust: searchInfo.cd_cust,
        dt_from: searchInfo.dt_from,
        dt_to: searchInfo.dt_to,
      };

      const component = (
        <SN_SCT_SIO_REG_001_POP_001 selectedData={selectedData} />
      );
      const result = await showResizablePopupModal({
        title: "발주적용 도움",
        children: component,
        validation: true,
      });
      if (!result || result === "close") return;

      const headerResult = result[0];
      const lineResult = result[1];

      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedRowNodeH && headerResult[0]) {
        focusedRowNodeH.setDataValue("cd_cust", headerResult[0].cd_cust);
        focusedRowNodeH.setDataValue(
          "nm_cust_sort",
          headerResult[0].nm_cust_sort
        );
        gridRefs.current["grid1"].current.applyBlock(focusedRowNodeH);
      }
      lineResult.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            addedNode.setDataValue("cd_matl", result.cd_matl);
            addedNode.setDataValue("nm_matl", result.nm_matl);
            addedNode.setDataValue(
              "dc_item_spec_matl",
              result.dc_item_spec_matl
            );
            addedNode.setDataValue(
              "dc_item_unit_matl",
              result.dc_item_unit_matl
            );
            addedNode.setDataValue("dt_dlv", result.dt_dlv);
            addedNode.setDataValue("qt", result.qt_app);
            addedNode.setDataValue("no_po", result.no_po);
            addedNode.setDataValue("no_line_pol", result.no_line_pol);
            addedNode.setDataValue("no_line_pod", result.no_line_pod);
            addedNode.setDataValue("fg_matl", result.fg_matl);
            addedNode.setDataValue("cd_pjt", result.cd_pjt);
            addedNode.setDataValue("nm_pjt", result.nm_pjt);
          }
        }
      });
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      const no_req = selectedRow.no_req;
      setSelectedDocu(no_req);
    }
  };

  const onCellValueChanged1 = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "cd_io") {
      let fg_io = "";
      for (const io of iotypeRef.current) {
        if (io.cd_io === rowData.cd_io) fg_io = io.fg_io;
      }
      if (fg_io) rowNode.setDataValue("fg_isu", fg_io);
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
          onCellValueChanged: onCellValueChanged1,
          onBeforeAddRow: onBeforeAddRow1,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRow2,
          onBtnDeleteClick: onGrid2BtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "의뢰정보" }}
            events={grids.grid1.events}
            header={
              <div>
                <SnButton
                  text={"발주적용"}
                  onClick={() => {
                    onGrid1BtnsClick("btnApply");
                  }}
                />
              </div>
            }
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_req"] }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{ rowSelection: "single", title: "상세정보" }}
            events={grids.grid2.events}
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
});

export default SN_SCT_SIO_REG_001;
